import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Switch, FormControlLabel, TextField, Button, Grid, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Divider,
  useTheme, useMediaQuery
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import SmsIcon from '@mui/icons-material/SmsOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import api from '../api';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const NotificationSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    notifyOnStatusChange: true,
    notifyOnAssignment: true,
    notifyOnCompletion: true,
    notifyOnCostUpdate: false,
    emailRecipients: [],
    smsRecipients: []
  });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [addRecipientDialog, setAddRecipientDialog] = useState(false);
  const [newRecipient, setNewRecipient] = useState({
    name: '',
    email: '',
    phone: '',
    type: 'email'
  });

  const notificationTypes = [
    { key: 'notifyOnStatusChange', label: 'Status Changes', description: 'When maintenance status is updated' },
    { key: 'notifyOnAssignment', label: 'Contractor Assignment', description: 'When a contractor is assigned' },
    { key: 'notifyOnCompletion', label: 'Request Completion', description: 'When maintenance is completed' },
    { key: 'notifyOnCostUpdate', label: 'Cost Updates', description: 'When costs are added or modified' }
  ];

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  const fetchNotificationSettings = async () => {
    try {
      const response = await api.get('/api/notification-settings');
      setSettings(response.data);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to fetch notification settings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = async (setting, value) => {
    try {
      const updatedSettings = { ...settings, [setting]: value };
      await api.put('/api/notification-settings', updatedSettings);
      setSettings(updatedSettings);
      setSnackbar({ open: true, message: 'Settings updated successfully!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update settings', severity: 'error' });
    }
  };

  const handleAddRecipient = async () => {
    try {
      const recipientData = {
        ...newRecipient,
        id: Date.now().toString()
      };

      const updatedRecipients = newRecipient.type === 'email' 
        ? [...settings.emailRecipients, recipientData]
        : [...settings.smsRecipients, recipientData];

      const updatedSettings = {
        ...settings,
        [newRecipient.type === 'email' ? 'emailRecipients' : 'smsRecipients']: updatedRecipients
      };

      await api.put('/api/notification-settings', updatedSettings);
      setSettings(updatedSettings);
      setSnackbar({ open: true, message: 'Recipient added successfully!', severity: 'success' });
      setAddRecipientDialog(false);
      setNewRecipient({ name: '', email: '', phone: '', type: 'email' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to add recipient', severity: 'error' });
    }
  };

  const handleDeleteRecipient = async (type, recipientId) => {
    try {
      const updatedRecipients = settings[type === 'email' ? 'emailRecipients' : 'smsRecipients']
        .filter(r => r.id !== recipientId);

      const updatedSettings = {
        ...settings,
        [type === 'email' ? 'emailRecipients' : 'smsRecipients']: updatedRecipients
      };

      await api.put('/api/notification-settings', updatedSettings);
      setSettings(updatedSettings);
      setSnackbar({ open: true, message: 'Recipient removed successfully!', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to remove recipient', severity: 'error' });
    }
  };

  const testNotification = async (type) => {
    try {
      await api.post('/api/notifications/test', { type });
      setSnackbar({ open: true, message: `Test ${type} notification sent!`, severity: 'success' });
    } catch (error) {
      const message = error.response?.status === 403
        ? 'Test notifications run for every account, so only ZenRentFlow support can send them.'
        : `Failed to send test ${type} notification`;
      setSnackbar({ open: true, message, severity: error.response?.status === 403 ? 'info' : 'error' });
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><Typography>Loading settings...</Typography></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ mb: { xs: 2, sm: 3 } }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          <NotificationsIcon sx={{ mr: { xs: 0.5, sm: 1 }, verticalAlign: 'middle', fontSize: { xs: 24, sm: 28 } }} />
          {isMobile ? 'Notifications' : 'Notification Settings'}
        </Typography>
      </Box>

      {/* General Notification Settings */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1.5, sm: 2 } }}
          >
            General Settings
          </Typography>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon sx={{ mr: 1 }} />
                    Email Notifications
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.smsNotifications}
                    onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SmsIcon sx={{ mr: 1 }} />
                    SMS Notifications
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Notification Types */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1.5, sm: 2 } }}
          >
            Notification Types
          </Typography>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {notificationTypes.map((type) => (
              <Grid item xs={12} md={6} key={type.key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings[type.key]}
                      onChange={(e) => handleSettingChange(type.key, e.target.checked)}
                      size={isMobile ? 'small' : 'medium'}
                    />
                  }
                  label={
                    <Box>
                      <Typography 
                        variant="subtitle2"
                        sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                      >
                        {type.label}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}
                      >
                        {type.description}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Email Recipients */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: { xs: 1.5, sm: 2 },
            gap: { xs: 1.5, sm: 0 }
          }}>
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              <EmailIcon sx={{ mr: { xs: 0.5, sm: 1 }, verticalAlign: 'middle', fontSize: { xs: 20, sm: 24 } }} />
              Email Recipients
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 1 },
              flexDirection: { xs: 'column', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                size={isMobile ? 'small' : 'small'}
                variant="outlined"
                onClick={() => testNotification('email')}
                fullWidth={isMobile}
              >
                Test Email
              </Button>
              <Button
                size={isMobile ? 'small' : 'small'}
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewRecipient({ name: '', email: '', phone: '', type: 'email' });
                  setAddRecipientDialog(true);
                }}
                fullWidth={isMobile}
              >
                Add Recipient
              </Button>
            </Box>
          </Box>
          <List>
            {settings.emailRecipients.map((recipient) => (
              <ListItem key={recipient.id}>
                <ListItemText
                  primary={recipient.name}
                  secondary={recipient.email}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteRecipient('email', recipient.id)}
                    color="error"
                    aria-label="Delete email recipient"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {settings.emailRecipients.length === 0 && (
              <ListItem>
                <ListItemText
                  primary="No email recipients"
                  secondary="Add recipients to receive email notifications"
                />
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>

      {/* SMS Recipients */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            mb: { xs: 1.5, sm: 2 },
            gap: { xs: 1.5, sm: 0 }
          }}>
            <Typography 
              variant="h6"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              <SmsIcon sx={{ mr: { xs: 0.5, sm: 1 }, verticalAlign: 'middle', fontSize: { xs: 20, sm: 24 } }} />
              SMS Recipients
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 1 },
              flexDirection: { xs: 'column', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                size={isMobile ? 'small' : 'small'}
                variant="outlined"
                onClick={() => testNotification('sms')}
                fullWidth={isMobile}
              >
                Test SMS
              </Button>
              <Button
                size={isMobile ? 'small' : 'small'}
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewRecipient({ name: '', email: '', phone: '', type: 'sms' });
                  setAddRecipientDialog(true);
                }}
                fullWidth={isMobile}
              >
                Add Recipient
              </Button>
            </Box>
          </Box>
          <List>
            {settings.smsRecipients.map((recipient) => (
              <ListItem key={recipient.id}>
                <ListItemText
                  primary={recipient.name}
                  secondary={recipient.phone}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteRecipient('sms', recipient.id)}
                    color="error"
                    aria-label="Delete SMS recipient"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {settings.smsRecipients.length === 0 && (
              <ListItem>
                <ListItemText
                  primary="No SMS recipients"
                  secondary="Add recipients to receive SMS notifications"
                />
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>

      {/* Add Recipient Dialog */}
      <Dialog 
        open={addRecipientDialog} 
        onClose={() => setAddRecipientDialog(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Add Notification Recipient</DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TextField
            label="Name"
            value={newRecipient.name}
            onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
            fullWidth
            margin="dense"
            sx={{ mt: { xs: 0.5, sm: 1 } }}
          />
          {newRecipient.type === 'email' ? (
            <TextField
              label="Email Address"
              value={newRecipient.email}
              onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
              fullWidth
              margin="dense"
              type="email"
              sx={{ mt: { xs: 1.5, sm: 2 } }}
            />
          ) : (
            <TextField
              label="Phone Number"
              value={newRecipient.phone}
              onChange={(e) => setNewRecipient({ ...newRecipient, phone: e.target.value })}
              fullWidth
              margin="dense"
              type="tel"
              sx={{ mt: { xs: 1.5, sm: 2 } }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setAddRecipientDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddRecipient} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Add Recipient
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationSettings; 